import React from 'react';
import { AlertTriangle, Search, RefreshCw, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

const BoolIcon = ({ value }) =>
  value ? (
    <CheckCircle className="h-4 w-4 text-green-500" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500" />
  );

export const MaintenanceTab = ({
  cleanupLoading,
  cleanupPreview,
  invalidProducts,
  onPreview,
  onRun,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        Data Cleanup - Invalid Products
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <p className="text-sm text-gray-600">
        Scan for and remove products that are missing required fields (price, image_url).
        These products cause errors in the client portal.
      </p>

      <div className="flex gap-4">
        <Button onClick={onPreview} disabled={cleanupLoading} variant="outline" data-testid="preview-cleanup-btn">
          {cleanupLoading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Scan for Invalid Products
        </Button>

        {invalidProducts.length > 0 && (
          <Button onClick={onRun} disabled={cleanupLoading} className="bg-red-600 hover:bg-red-700" data-testid="run-cleanup-btn">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete {invalidProducts.length} Invalid Products
          </Button>
        )}
      </div>

      {cleanupPreview && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="font-medium mb-2">
            Scan Results: {cleanupPreview.count} invalid products found
          </p>
          {cleanupPreview.note && <p className="text-sm text-gray-600 mb-4">{cleanupPreview.note}</p>}

          {invalidProducts.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>Has Price</TableHead>
                  <TableHead>Has Retail Price</TableHead>
                  <TableHead>Has Image</TableHead>
                  <TableHead>Missing Fields</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invalidProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell><BoolIcon value={product.has_price} /></TableCell>
                    <TableCell><BoolIcon value={product.has_retail_price} /></TableCell>
                    <TableCell><BoolIcon value={product.has_image_url} /></TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-red-600">
                        {product.missing_fields?.join(', ') || 'None'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);
